/**
 * HTTP request (fetch) helpers.
 */

import { getCookie } from './cookie';

/**
 * Wrapped fetch, used for exported GET and POST helpers.
 *
 * @param {string} url - URL to fetch.
 * @param {string} method - HTTP method (GET, POST...).
 * @param {Object} [extraHeaders] - Any extra headers to add.
 * @param {string} [body] - Body, e.g. for POST requests.
 * @return {Promise} Resolved with the response data.
 */
async function _fetch(url, method, extraHeaders = {}, body) {
  const params = {
    method,
    body,
    credentials: 'same-origin',
    headers: Object.assign(
      {
        'X-REQUESTED-WITH': 'XMLHttpRequest',
        'X-CSRFTOKEN': getCookie('csrftoken'),
      },
      extraHeaders,
    ),
  };
  const response = await fetch(url, params);

  if (!response.ok) {
    throw new Error(`Got response ${response.status} ${response.statusText}`);
  }

  return params.headers.Accept === 'application/json' ||
    params.headers['Content-Type'] === 'application/json'
    ? response.json()
    : response.text();
}

/**
 * Run a GET request.
 *
 * @param {string} url - URL to get.
 * @param {Object} [extraHeaders] - Any extra headers to send.
 * @return {Promise} Resolved with the response data.
 */
export function httpGet(url, extraHeaders = {}) {
  return _fetch(url, 'GET', extraHeaders);
}

/**
 * Run a POST request.
 *
 * @param {string} url - URL to get.
 * @param {Object} [extraHeaders] - Any extra headers to send.
 * @param {string} [body] - POST body.
 * @return {Promise} Resolved with the response data.
 */
export function httpPost(url, extraHeaders = {}, body) {
  return _fetch(url, 'POST', extraHeaders, body);
}

/**
 * Run a GET request to fetch JSON.
 *
 * Just like a regular fetch but with `application/json` headers set.
 *
 * @param {string} url - URL to get.
 * @param {Object} [extraHeaders] - Any extra headers to send.
 * @return {Promise} Resolved with the JSON response.
 */
export function httpGetJSON(url, extraHeaders = {}) {
  return _fetch(
    url,
    'GET',
    Object.assign(
      {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      extraHeaders,
    ),
  );
}

/**
 * Run a JSON POST request.
 *
 * Just like a regular fetch but with `application/json` headers set.
 *
 * @param {string} url - URL to get.
 * @param {Object} [extraHeaders] - Any extra headers to send.
 * @param {string} [body] - POST body.
 * @return {Promise} Resolved with the JSON response.
 */
export function httpPostJSON(url, extraHeaders = {}, body) {
  return _fetch(
    url,
    'POST',
    Object.assign(
      {
        Accept: 'application/json',
        'Content-Type': 'application/json',
      },
      extraHeaders,
    ),
    body,
  );
}
